/**
 * Cache for resolved Firebase Storage download URLs, keyed by the object PATH (the "content url" key).
 *
 * The immutable content we serve — agent avatars, the shared social galleries, generated media — lives at
 * TIMESTAMPED, never-reused paths, so each path resolves to a STABLE download URL. Resolving it once and
 * reusing it cuts repeated `getDownloadURL` Storage-API calls (and the flash of a re-resolving <img>) as the
 * same image mounts all over the app. In-memory Map (instant) + a bounded localStorage mirror so the cache
 * survives navigations and reloads. Safe because the paths are effectively immutable — a changed asset gets
 * a new path (new cache key), so a stale URL is never served.
 */
const LS_KEY = 'wisetree_storage_urls_v1';
const MAX_ENTRIES = 500; // bound the localStorage mirror
const mem = new Map<string, string>();
let hydrated = false;

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
    if (raw) for (const [p, u] of Object.entries(JSON.parse(raw) as Record<string, string>)) mem.set(p, u);
  } catch { /* corrupt/disabled — in-memory still works */ }
}

function persist(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const obj: Record<string, string> = {};
    let i = 0;
    for (const [p, u] of mem) { if (i++ >= MAX_ENTRIES) break; obj[p] = u; }
    localStorage.setItem(LS_KEY, JSON.stringify(obj));
  } catch { /* quota exceeded / disabled — the in-memory cache still functions */ }
}

/** The cached download URL for a Storage path, or null if not resolved yet. */
export function getCachedUrl(path: string): string | null {
  hydrate();
  return mem.get(path) ?? null;
}

/** Remember a resolved download URL for a path. */
export function setCachedUrl(path: string, url: string): void {
  hydrate();
  mem.set(path, url);
  persist();
}

/** Clear the cache (tests / a hard refresh of assets). */
export function clearStorageUrlCache(): void {
  mem.clear();
  hydrated = false;
  try { if (typeof localStorage !== 'undefined') localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}
